<template>
  <module-page v-bind="pageParams" :customBreadcrumbs="customBreadcrumbs">
    <v-layout row wrap>
      <v-flex xs12 md8 lg8>
        <v-tabs class="page__tabs" hide-slider v-model="selectedTab">
          <v-tab
            v-for="(tab, i) in pageParams.tabs"
            @click="goToTab(tab.value)"
            :to="`${tab.value}`"
            :key="i"
            active-class="support--background white--text"
          >
            {{
              tab.title.mobile
                ? mobile
                  ? tab.title.mobile
                  : tab.title.notMobile
                : tab.title
            }}
          </v-tab>
        </v-tabs>
        <v-form
          v-if="!newTicketNumber"
          v-model="formValid"
          @submit.prevent="submit"
          ref="supportForm"
          lazy-validation
          class="pr-3"
        >
          <empty-state-card v-if="ombudsman.length == 0">
            <div slot="message">
              Você <b>não possui</b> solicitações para abertura de ouvidoria.
            </div>
          </empty-state-card>
          <v-card v-else class="mt-2 mb-5">
            <v-card-title class="pt-headline support--color pb-1">
              {{ $t('pages.support.ticketOmbudsman.title') }}
            </v-card-title>
            <v-card-text class="pt-1">
              <v-layout row wrap>
                <v-flex xs12 sm5 md6 lg5 xl3>
                  <TextField
                    required
                    auto-grow
                    solo
                    type="tel"
                    mask="!##############"
                    label="Solicitação original"
                    v-model="ticketRef"
                    :rules="rule.required"
                    :errorMessages="ombudsmanError"
                  ></TextField>
                </v-flex>
                <v-flex xs12>
                  <label required class="v-label --required__label">
                    {{ $t('pages.support.messageInput') }}
                  </label>
                  <div
                    class="ticket__input"
                    :class="{ ticket__input__error: !formValid }"
                  >
                    <v-textarea
                      required
                      auto-grow
                      solo
                      v-model="model.public.content"
                      :rules="rule.required"
                    ></v-textarea>
                    <file-input
                      @input="updateFiles"
                      @fileToLarge="handleFileToLarge"
                      @fileInvalid="handleFileInvalid"
                      :maxFileSize="maxFileSize"
                      :label="$t('labels.fileInput.title')"
                      :btn-label="$t('labels.fileInput.selectBtn')"
                      :drag-and-drop-label="
                        $t('labels.fileInput.dragAndDropAction')
                      "
                      class="ticket__file-input"
                      btn-class="support--background white--text btn-md"
                      drag-and-drop-class="support--color btn-md"
                      append-icon="attachment"
                      loadColor="blue"
                    ></file-input>
                  </div>
                </v-flex>
                <v-flex xs12>
                  <span class="error--text">
                    {{ fileError }}
                  </span>
                  <file-preview
                    show-remove
                    :items="files"
                    btn-classes="support--background white--text btn-md"
                    @remove="removeFile"
                  ></file-preview>
                </v-flex>
              </v-layout>
            </v-card-text>
            <v-card-actions>
              <v-btn dark class="support--background btn-md" type="submit">
                <v-progress-circular
                  color="white"
                  indeterminate
                  v-if="loadingApp"
                ></v-progress-circular>
                <span v-else>
                  {{ $t('actions.send') }}
                </span>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
        <v-layout column align-start justify-center v-else class="pt-4 pl-1">
          <p class="text-sm-center" style="color: var(--label-color)">
            {{ $t('responses.ticketFormSuccess', { newTicketNumber }) }}
          </p>
        </v-layout>
      </v-flex>
      <v-flex xs12 md4 lg4 class="mt-4 mb-4">
        <faq-card
          :items="faqItems"
          :loading="faqLoading"
          :error="faqError"
          :params="faqCardParams"
          :errorText="$t('responses.error')"
          @faqSelected="goToFaq"
          :retryFunction="getFaqs"
        ></faq-card>
      </v-flex>
    </v-layout>
  </module-page>
</template>

<script>
import bus from '@/utils/bus';
import enums from '@/utils/enums';
import { APP, USER, TICKET_CRM, CONFIG } from '@/utils/constants';
import faqMixin from '@/mixins/modules/faq';
import supportMixin from '@/mixins/modules/support';
import profile from '@/mixins/modules/profile';
import upload from '@/services/upload';

import ModulePage from '@/components/layout/Page';
import FilePreview from '@/components/layout/FilePreview';
import FaqCard from '@/components/modules/FaqCard';
import SelectField from '@/components/fields/Select';
import FileInput from '@/components/fields/FileInput';
import TextArea from '@/components/fields/TextArea';
import TextField from '@/components/fields/TextField';
import EmptyStateCard from '@/components/layout/EmptyStateCard';

import ScreenLoading from '@/components/loaders/ScreenLoading';
import getCurrentBandwidth from '@/utils/bandwidth';

export default {
  name: 'ombudsmanForm',
  mixins: [faqMixin, supportMixin],
  components: {
    ModulePage,
    FaqCard,
    SelectField,
    TextField,
    TextArea,
    ScreenLoading,
    FileInput,
    FilePreview,
    EmptyStateCard,
  },
  computed: {
    customBreadcrumbs() {
      const path = this.$route.path;
      const tab = this.pageParams.tabs.find((tab) => tab.value == path);
      if (tab) {
        const title = tab.title.mobile
          ? this.mobile
            ? tab.title.mobile
            : tab.title.notMobile
          : tab.title;
        return [{ text: title }];
      } else {
        return [{ text: this.$t('pages.support.breadcrumb') }];
      }
    },
    ombudsman() {
      return this.supportItems.ombudsman;
    },
  },
  data() {
    return {
      ticketRef: '',
      model: {
        summary: '',
        public: {
          content: '',
          attachments: [],
        },
      },
      productsStates: {},
      files: [],
      newTicketNumber: 0,
      fileError: '',
      ombudsmanError: '',
      selectedTab: '',
      formValid: false,
      maxFileSize: enums.maxUploadFileSize,
      pageParams: this.$store.getters[APP.PAGES].support,
      faqCardParams: this.$store.getters[APP.APP_MODULES].faq,
      ticketSubjects: [],
      useTicketProductState: false,
    };
  },
  created() {
    bus.$on('unit:change', this.getItems);
    bus.$on('unit:update', this.getItems);
    const navigationRules = this.$store.getters[CONFIG.GET].navigationRules;
    this.ticketSubjects = navigationRules.ticketSubjects;
    this.useTicketProductState = navigationRules.useTicketProductState;
    this.model.summary = this.ticketSubjects[0].text;
    this.getFaqs();
    this.getItems();
  },
  beforeDestroy() {
    bus.$off('unit:change', this.getItems);
    bus.$off('unit:update', this.getItems);
  },
  methods: {
    getItems(unitObj = null) {
      this.getOctadeskAllProposersTickets();
    },
    handleFileToLarge() {
      this.fileError = this.$t('responses.filesToLarge');
    },
    handleFileInvalid(file) {
      this.fileError = this.$t('responses.fileInvalid', { file });
    },
    handleTicketNotFound() {
      this.ombudsmanError =
        'Não encontramos uma solicitação relacionada ao número informado. Verifique se o número da solicitação está correto, ou abra uma nova solicitação.';
    },
    handleInvalidTicket() {
      this.ombudsmanError =
        'Esta solicitação está dentro do prazo de atendimento. Você poderá interagir através do botão "Acompanhe suas solicitações".';
    },
    goToTab(path) {
      if (path != this.$route.path) {
        this.$router.push(path);
      } else {
        this.resetModel();
      }
    },
    updateFiles(v) {
      this.fileError = '';
      if (v) this.files.push(...v);
    },
    removeFile(v) {
      this.fileError = '';
      this.files.splice(this.files.indexOf(v), 1);
    },
    async getProductLocationState(code) {
      const params = {
        filter: { code },
        desiredFields: { code: 1, address: 1 },
      };
      const url = `${process.env.CRUD_API_GET}/product`;
      const result = await this.requestService(url, 'GET', { params });
      const [item] = result.items;

      if (!item || !item.address) {
        throw new Error(
          `This product does not exist on database. PRODUCT_CODE: ${code}`
        );
      }

      return item.address.uf;
    },
    saveFiles() {
      const files = this.files.map((o) => o.binary);
      return upload(files, enums.fileCategory.TICKET_ATTACHMENT);
    },
    checkOpenedTickets(ticketRef) {
      return (
        this.supportItems.directed.find((t) => t.number == this.ticketRef) ||
        this.supportItems.inProgress.find((t) => t.number == this.ticketRef) ||
        this.supportItems.inAnalysis.find((t) => t.number == this.ticketRef)
      );
    },
    async submit() {
      let attachmentsUrls = [];
      try {
        this.ombudsmanError = '';
        this.fileError = '';
        if (!this.$refs.supportForm.validate()) return;
        let originalTicket = this.ombudsman.find(
          (t) => t.number == this.ticketRef
        );
        if (!originalTicket) {
          originalTicket = this.checkOpenedTickets(this.ticketRef);
          if (!originalTicket) {
            this.handleTicketNotFound();
          } else {
            this.handleInvalidTicket();
          }
          return;
        }

        const { product } = this.selectedUnit;
        let productState;
        if (this.useTicketProductState) {
          if (!this.productsStates[product.code]) {
            this.productsStates[product.code] =
              await this.getProductLocationState(product.code);
          }
          productState = this.productsStates[product.code];
          if (!productState) return;
        }

        let { content, attachments } = this.model.public;
        if (this.files.length > 0) {
          attachmentsUrls = attachments = await this.saveFiles();
        }
        const summary = `Ouvidoria | ${originalTicket.summary} | #${this.ticketRef}`;
        const payload = {
          summary,
          comments: {
            public: { content, attachments },
          },
          productState,
        };
        const result = await this.sendTicket(payload);
        if (result) {
          this.newTicketNumber = result.number;
          this.$store.commit(TICKET_CRM.NEW_TICKET_CREATED);
          this.createTrace(enums.logTraceActions.ombudsmanCreated, {
            number: result.number,
            summary: result.summary,
            description: result.descriptionByText,
            octadeskNumberUrl: result.octadeskNumberUrl,
            originalTicket: this.ticketRef,
          });
        }
      } catch (error) {
        const bandwidthInMbps = await getCurrentBandwidth();
        this.createTrace(enums.logTraceActions.ombudsmanError, {
          originalTicket: this.ticketRef,
          summary: this.model.summary,
          content: this.model.public,
          files: this.files.map((file) => ({
            name: file.binary.name,
            size: file.binary.size,
            type: file.binary.type,
          })),
          error: {
            message: error.message,
            stack: error.stack,
          },
          connectionSpeed: `${bandwidthInMbps} MB`,
        });

        bus.$emit('snackbar:open', {
          text: this.$t('responses.error'),
          color: enums.snackbarColors.error,
        });
      }
    },
    resetModel() {
      this.newTicketNumber = 0;
      this.formValid = false;
      this.files = [];
      this.model.summary = '';
      this.model.public.content = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.ticket {
  &__input {
    border: 1px lightgray solid;
    border-radius: 5px;

    padding: 0px 0px 5px;

    &__error {
      border: 1px #ff5252 solid;
    }

    >>> .v-input__slot {
      border-top-left-radius: 5px;
      border-top-right-radius: 5px;
      box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.02),
        0 1px 5px 0 rgba(0, 0, 0, 0.02) !important;

      textarea {
        margin: 0 !important;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/support/ticket/OmbudsmanForm.vue